import { Contrast, Droplets, Sun } from 'lucide-react'
import { AdjustmentChannel, ADJUSTMENT_RANGE, isIdentityAdjustments } from '@/core/document'
import { useEditSession } from '@/core/store/editSession'
import { TEST_IDS } from '@/lib/testIds'
import {
  commitSelectedAdjustments,
  resetSelectedAdjustments,
  setSelectedAdjustment,
} from '@/tools/adjustments/actions'
import { selectSelectedAdjustments } from '@/tools/adjustments/selectors'
import { Button } from '@/ui/shadcn/button'
import { IconSlider } from '@/ui/IconSlider'
import type { ReactNode } from 'react'

function AdjustmentSlider({
  channel,
  label,
  value,
  icon,
}: {
  channel: AdjustmentChannel
  label: string
  value: number
  icon: ReactNode
}) {
  return (
      <IconSlider
        min={ADJUSTMENT_RANGE.min}
        max={ADJUSTMENT_RANGE.max}
        step={1}
        value={value}
        startIcon={icon}
        endIcon={icon}
        formatValue={(next) => next > 0 ? `+${next}` : String(next)}
        showZeroTick
        data-testid={TEST_IDS.adjustmentSlider(channel)}
        aria-label={`${label} adjustment`}
        onValueChange={(next) => setSelectedAdjustment(channel, next)}
        onValueCommit={() => commitSelectedAdjustments()}
      />
  )
}

export function AdjustmentsPanel() {
  const adjustments = useEditSession(selectSelectedAdjustments)
  return (
    <section className="space-y-5" aria-labelledby="adjustments-heading">
      <h2 id="adjustments-heading" className="font-semibold">Adjustments</h2>
      <AdjustmentSlider channel={AdjustmentChannel.Brightness} label="Brightness" value={adjustments.brightness} icon={<Sun className="size-4" />} />
      <AdjustmentSlider channel={AdjustmentChannel.Contrast} label="Contrast" value={adjustments.contrast} icon={<Contrast className="size-4" />} />
      <AdjustmentSlider channel={AdjustmentChannel.Saturation} label="Saturation" value={adjustments.saturation} icon={<Droplets className="size-4" />} />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid={TEST_IDS.adjustmentsReset}
        disabled={isIdentityAdjustments(adjustments)}
        onClick={resetSelectedAdjustments}
      >
        Reset
      </Button>
    </section>
  )
}
